import { getCurrentUserLocale } from 'utils/globalLocale';
import { interpolate } from 'utils/i18nUtils';

/**
 * Convert a Date to YYYY-MM-DD string in local timezone.
 * Avoids the UTC shift bug from toISOString().split('T')[0].
 */
export function toLocalDateStr(d) {
  const date = d instanceof Date ? d : new Date(d)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

/**
 * Parse a 'YYYY-MM-DD' string into a Date at local midnight.
 * Returns null unless the string names a real calendar day: '2026-02-31'
 * would otherwise roll over to March 3 (in the browser and in Rails' zone.parse).
 */
export function parseLocalDateStr(str) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(typeof str === 'string' ? str : '')
  if (!m) return null
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return toLocalDateStr(date) === str ? date : null
}

/**
 * 格式化對話列表的日期時間
 * 根據時間距離顯示不同格式：
 * - 今天：顯示時間（如 "14:30"）
 * - 昨天：顯示 "昨天"
 * - 7 天內：顯示 "N 天前"
 * - 7 天以上：顯示月日（如 "12月14日"）
 *
 * @param {string} dateString - ISO 8601 日期字串
 * @param {object} translations - 翻譯對象，包含 yesterday 和 days_ago
 * @returns {string} 格式化後的日期字串
 */
export function formatConversationDate(dateString, translations = {}) {
  if (!dateString) {
    return translations?.no_messages_yet || 'No messages yet';
  }

  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  // 獲取當前語言設定
  const locale = getCurrentUserLocale() || 'zh-TW';

  if (diffDays === 0) {
    // 今天：顯示時間
    return date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } else if (diffDays === 1) {
    // 昨天
    return translations?.yesterday || 'Yesterday';
  } else if (diffDays < 7) {
    // 7 天內：顯示天數
    return interpolate(translations?.messaged_days_ago || '%{count} days ago', { count: diffDays });
  } else {
    // 7 天以上：顯示月日
    return date.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric'
    });
  }
}

/**
 * 格式化訊息時間
 * 顯示完整的日期時間（如 "12月14日 14:30"）
 *
 * @param {string} dateString - ISO 8601 日期字串
 * @returns {string} 格式化後的日期時間字串
 */
export function formatMessageTime(dateString) {
  if (!dateString) return '';

  const date = new Date(dateString);
  const locale = getCurrentUserLocale() || 'zh-TW';

  return date.toLocaleString(locale, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * 格式化完整日期時間（用於詳細資訊）
 * 顯示年月日時分（如 "2025年12月14日 14:30"）
 *
 * @param {string} dateString - ISO 8601 日期字串
 * @returns {string} 格式化後的完整日期時間字串
 */
export function formatFullDateTime(dateString) {
  if (!dateString) return '';

  const date = new Date(dateString);
  const locale = getCurrentUserLocale() || 'zh-TW';

  return date.toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Lesson date and time in a given timezone, e.g. "10/2（週五）20:00".
 * Used where a lesson time is shown in the teacher's or viewer's zone.
 *
 * @param {string|Date} value - ISO 8601 string or Date
 * @param {string} [timeZone] - IANA zone; the browser's when omitted
 * @returns {string}
 */
export function formatLessonDateTime(value, timeZone) {
  if (!value) return '';

  const locale = getCurrentUserLocale() || 'zh-TW';

  // 12-hour in English, 24-hour in Chinese, matching how each page prints times.
  return new Date(value).toLocaleString(locale, {
    timeZone: timeZone || undefined,
    month: 'numeric',
    day: 'numeric',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: locale.startsWith('en')
  });
}

/**
 * A calendar day ("YYYY-MM-DD") with its weekday, in the page's language:
 * "2026年10月03日 週六", "Sat, October 03, 2026". Parsed as a local day, since
 * new Date("2026-10-03") is UTC midnight and shows the previous day west of UTC.
 *
 * @param {Date|string} value - a local Date, or YYYY-MM-DD
 * @param {string} [locale] - defaults to the rendered page locale
 * @returns {string}
 */
export function formatDateOnly(value, locale = getCurrentUserLocale() || 'zh-TW') {
  if (!value) return '';

  // A Date (what the calendar picks) is already a local day; a string is read
  // as one rather than as UTC midnight.
  let day = value;
  if (!(value instanceof Date)) {
    const [y, m, d] = String(value).split('-').map(Number);
    day = new Date(y, m - 1, d);
  }

  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: '2-digit',
    weekday: 'short'
  }).format(day);
}
